import { MetaTemplate } from '../services/meta-whatsapp.service';

// Busca e corpo do template de WhatsApp para o modal e a linha do tempo da sequência (M4) num
// lugar só — as cópias por tela já divergiam no idioma e no rótulo do "Meu link".

/** Variável do template que vale o "Meu link" do passo (Decisão 7). */
export const MY_LINK_TOKEN = 'meu_link';
export const MY_LINK_LABEL = 'Meu link (desta mensagem)';

/**
 * O template é nome + idioma: a Meta aprova o mesmo nome em mais de um idioma. Sem o idioma (ou
 * sem um template naquele idioma), vale o primeiro com o nome.
 */
export function findTemplate(templates: MetaTemplate[], name: string | null, language: string | null): MetaTemplate | undefined {
  // Com idioma, o mesmo nome em outro idioma é outro template (outros {{n}}, outro corpo): sem
  // fallback. Só o passo antigo, sem idioma gravado, é achado pelo nome.
  return language
    ? templates.find(t => t.name === name && t.language === language)
    : templates.find(t => t.name === name);
}

export function templateBodyText(template: MetaTemplate | undefined): string {
  return (template?.components || []).find(c => c.type === 'BODY')?.text || '';
}

// ---------------------------------------------------------------- variáveis do template (bug #132000, 09/10)

/** Valor fixo digitado no passo, no lugar de um dado do lead. Espelha WhatsappTemplateParameters (back). */
export const FIXED_PREFIX = 'text:';

/** Dado do lead que vale para a variável "de nome" do passo salvo antes do mapeamento nomeado. */
export const DEFAULT_NAME_TOKEN = 'first_name';

const NAME_LIKE = /^(?:nome|name|first_?name|primeiro_?nome|nome_?(?:do_?)?(?:cliente|lead|contato)|cliente|contato|lead)$/i;
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Os nomes de `{{x}}` de um texto, na ordem em que aparecem (com repetição). */
function placeholderIds(text: string): string[] {
  const ids: string[] = [];
  const re = new RegExp(PLACEHOLDER.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || '')) !== null) {
    ids.push(m[1]);
  }
  return ids;
}

/** Mapeamento salvo no passo: lista antiga (n-ésimo item = {{n}} do corpo) ou mapa vaga → token / `text:…`. */
export type TemplateMapping = string[] | { [key: string]: string } | null | undefined;

/**
 * Uma variável ("vaga") do template: `{{nome}}` ou `{{1}}` do corpo ou do cabeçalho, a mídia do
 * cabeçalho ou o sufixo/código de um botão. A `key` é a mesma que o back usa para montar a chamada.
 */
export interface TemplateSlot {
  key: string;
  id: string;
  component: 'header' | 'body' | 'button';
  /** Nome do `{{nome}}` (nomeado); null no posicional. */
  name: string | null;
  type: 'text' | 'image' | 'video' | 'document' | 'url' | 'copy_code';
  /** Posição do botão. */
  index: number | null;
  /** Como aparece para o cliente: "{{nome}}", "Cabeçalho {{1}}", "Botão 2 (link)". */
  label: string;
  /** Exemplo que a Meta aprovou, quando veio. */
  example?: string;
}

function namedExamples(component: any): { [name: string]: string } {
  const out: { [name: string]: string } = {};
  const list = component?.example?.body_text_named_params || component?.example?.header_text_named_params || [];
  (list as any[]).forEach(p => out[p.param_name] = p.example);
  return out;
}

function isNamedTemplate(template: MetaTemplate): boolean {
  const format = String((template as any)?.parameter_format || '').toUpperCase();
  if (format) {
    return format === 'NAMED';
  }
  return (template?.components || []).some(c =>
    (c?.example?.body_text_named_params || c?.example?.header_text_named_params)?.length
    || placeholderIds(String(c?.text || '')).some(id => !/^\d+$/.test(id))
  );
}

function textSlots(component: 'header' | 'body', comp: any, named: boolean): TemplateSlot[] {
  const ids = Array.from(new Set(placeholderIds(String(comp?.text || ''))));
  const prefix = component === 'header' ? 'Cabeçalho ' : '';
  if (!named) {
    const positional = (comp?.example?.body_text?.[0] || comp?.example?.header_text || []) as string[];
    return ids.filter(id => /^\d+$/.test(id))
      .sort((a, b) => Number(a) - Number(b))
      .map(id => ({ key: `${component}:${id}`, id, component, name: null, type: 'text', index: null,
        label: `${prefix}{{${id}}}`, example: positional[Number(id) - 1] }));
  }
  const examples = namedExamples(comp);
  return ids.map(id => ({ key: `${component}:${id}`, id, component, name: id, type: 'text', index: null,
    label: `${prefix}{{${id}}}`, example: examples[id] }));
}

/** As variáveis do template, na ordem da Meta: cabeçalho, corpo, botões. */
export function templateSlots(template: MetaTemplate | undefined): TemplateSlot[] {
  if (!template) {
    return [];
  }
  const named = isNamedTemplate(template);
  const slots: TemplateSlot[] = [];

  (template.components || []).forEach(comp => {
    const type = String(comp?.type || '').toUpperCase();
    if (type === 'HEADER') {
      const format = String(comp?.format || 'TEXT').toUpperCase();
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
        const media = format.toLowerCase() as 'image' | 'video' | 'document';
        const names = { image: 'imagem', video: 'vídeo', document: 'documento' };
        slots.push({ key: 'header:media', id: 'media', component: 'header', name: null, type: media, index: null,
          label: `Cabeçalho (${names[media]}: link)` });
      } else if (format === 'TEXT') {
        slots.push(...textSlots('header', comp, named));
      }
    }
    if (type === 'BODY') {
      slots.push(...textSlots('body', comp, named));
    }
    if (type === 'BUTTONS') {
      (comp.buttons || []).forEach((button: any, index: number) => {
        const buttonType = String(button?.type || '').toUpperCase();
        if (buttonType === 'URL' && /\{\{\s*[A-Za-z0-9_]+\s*\}\}/.test(String(button?.url || ''))) {
          slots.push({ key: `button:${index}`, id: String(index), component: 'button', name: null, type: 'url', index,
            label: `Botão “${button.text || index + 1}” (fim do link)`, example: (button.example || [])[0] });
        }
        if (buttonType === 'COPY_CODE') {
          slots.push({ key: `button:${index}`, id: String(index), component: 'button', name: null, type: 'copy_code', index,
            label: 'Botão “Copiar código”', example: Array.isArray(button.example) ? button.example[0] : button.example });
        }
      });
    }
  });

  return slots;
}

/**
 * O mapeamento salvo, vaga a vaga (espelha `WhatsappTemplateParameters::mapping`). Lista antiga: o
 * item n vai para a n-ésima variável do corpo. Variável "de nome" sem nada recebe {first_name} e
 * entra em `defaulted` — é o passo salvo antes do mapeamento nomeado.
 */
export function normalizeTemplateMapping(stored: TemplateMapping, slots: TemplateSlot[]): { map: { [key: string]: string }; defaulted: string[] } {
  const map: { [key: string]: string } = {};
  if (Array.isArray(stored)) {
    slots.filter(s => s.component === 'body').forEach((slot, i) => {
      if (stored[i]) {
        map[slot.key] = stored[i];
      }
    });
  } else if (stored) {
    slots.forEach(slot => {
      if (stored[slot.key]) {
        map[slot.key] = stored[slot.key];
      }
    });
  }

  const defaulted: string[] = [];
  slots.forEach(slot => {
    if (!map[slot.key] && slot.name && NAME_LIKE.test(slot.name)) {
      map[slot.key] = DEFAULT_NAME_TOKEN;
      defaulted.push(slot.key);
    }
  });
  return { map, defaulted };
}

/** O mapeamento salvo é a lista antiga, ou está vazio (passo de antes do mapeamento nomeado). */
export function isLegacyMapping(stored: TemplateMapping): boolean {
  return !stored || Array.isArray(stored) || !Object.keys(stored).length;
}

/** Valor que vai para a variável: o texto fixo, ou o dado do lead/passo (`token`). */
export function mappingSource(value: string | undefined): { fixed: boolean; value: string } {
  if ((value || '').startsWith(FIXED_PREFIX)) {
    return { fixed: true, value: value.substring(FIXED_PREFIX.length) };
  }
  return { fixed: false, value: value || '' };
}

/** O texto (corpo ou cabeçalho) com cada `{{x}}` trocado pelo que `fill` devolve para a vaga (ou mantido). */
export function fillTemplateText(text: string, component: 'header' | 'body', map: { [key: string]: string },
                                 fill: (value: string) => string): string {
  return (text || '').replace(PLACEHOLDER, (match, id) => {
    const value = map[`${component}:${id}`];
    return value ? fill(value) : match;
  });
}
